import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import api from "@/services/api";
import { useTranslation } from "@/context/TranslationContext";

interface PortfolioItem {
  id: string;
  title: string;
  category: string;
  description: string;
  thumbnail_url: string | null;
  tech_stack: string[];
  featured: boolean;
  year: string | null;
  client_name: string | null;
}

const convertDriveLink = (url: string) => {
  console.log('Converting URL:', url);
  if (url.includes('drive.google.com')) {
    let fileId = null;

    // Format: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
    const fileMatch = url.match(/\/file\/d\/([^\/]+)/);
    if (fileMatch) {
      fileId = fileMatch[1];
      console.log('Matched file format, fileId:', fileId);
    } else {
      // Format: https://drive.google.com/open?id=FILE_ID or https://drive.google.com/uc?id=FILE_ID
      const idMatch = url.match(/[?&]id=([^&]+)/);
      if (idMatch) {
        fileId = idMatch[1];
        console.log('Matched id format, fileId:', fileId);
      }
    }

    if (fileId) {
      const converted = `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`;
      console.log('Converted to:', converted);
      return converted;
    } else {
      console.log('No fileId found');
    }
  } else {
    console.log('Not a Google Drive URL');
  }
  return url;
};

export default function Portfolio() {
  const { t } = useTranslation();
  const [items, setItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = useMemo(() => {
    const unique = Array.from(new Set(items.map(i => i.category).filter(Boolean)));
    return ["All", ...unique];
  }, [items]);

  useEffect(() => {
    api.get("/portfolio")
      .then(res => setItems(res.data.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const matchesCategory = (itemCategory: string, filterCategory: string) => {
    const item = itemCategory.trim().toLowerCase();
    const filter = filterCategory.trim().toLowerCase();
    if (item === filter || item.includes(filter) || filter.includes(item)) return true;
    const itemTokens = item.split(/[\s,/&-]+/).filter(t => t.length >= 3);
    const filterTokens = filter.split(/[\s,/&-]+/).filter(t => t.length >= 3);
    return filterTokens.some(ft => itemTokens.some(it => it.includes(ft) || ft.includes(it)));
  };

  const filtered = activeCategory === "All"
    ? items
    : items.filter((item) => matchesCategory(item.category, activeCategory));

  return (
    <div className="min-h-screen bg-deep-black font-inter">
      <Navbar />

      <section className="relative pt-32 pb-20 px-4 md:px-12 lg:px-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://p.turbosquid.com/ts-thumb/v1/BpuvfH/yHU1mqDi/0/jpg/1564675940/1920x1080/fit_q87/174f4563659772ff3bf1f5a15b3ec10cdfbd5be4/0.jpg"
            alt="Portfolio background"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-deep-black/60 via-deep-black/40 to-deep-black" />
          <div className="absolute inset-0 bg-circuit-pattern opacity-30" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block text-electric-blue text-xs font-semibold uppercase tracking-[0.2em] mb-5 px-3 py-1.5 border border-electric-blue/25 rounded-full bg-electric-blue/5">
              {t("portfolio.hero_badge")}
            </span>
            <h1 className="font-black text-white leading-tight mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              {t("portfolio.hero_title")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric-blue to-cyber-blue">{t("portfolio.hero_highlight")}</span>
            </h1>
            <p className="text-silver text-base max-w-2xl mx-auto leading-relaxed">
              {t("portfolio.hero_description")}
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-12 relative z-10">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-electric-blue text-deep-black shadow-glow-blue-sm"
                    : "bg-dark-card border border-dark-border text-silver hover:text-white hover:border-electric-blue/30"
                }`}
                style={activeCategory !== cat ? {
                  backgroundColor: "var(--color-bg-card)",
                  color: "var(--color-text-secondary)",
                } : {}}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item) => (
                <Link
                  key={item.id}
                  to={`/portfolio/${item.id}`}
                  className="group bg-dark-card border border-dark-border rounded-2xl overflow-hidden hover:border-electric-blue/30 transition-all duration-300"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={convertDriveLink(item.thumbnail_url || "") || "https://via.placeholder.com/800x500?text=No+Image"}
                      alt={item.title}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-deep-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs px-2.5 py-1 rounded-full bg-electric-blue/10 border border-electric-blue/20 text-electric-blue font-medium">
                        {item.category}
                      </span>
                      {item.featured && (
                        <span className="text-xs px-2.5 py-1 rounded-full bg-cyber-green/10 border border-cyber-green/20 text-cyber-green font-medium">
                          {t("portfolio.featured")}
                        </span>
                      )}
                    </div>
                    <h3 className="text-white font-bold text-base mb-2 group-hover:text-electric-blue transition-colors" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {item.title}
                    </h3>
                    <p className="text-silver text-sm leading-relaxed line-clamp-2 mb-4">
                      {item.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(Array.isArray(item.tech_stack) ? item.tech_stack : []).slice(0, 4).map((tech) => (
                        <span key={tech} className="text-xs px-2 py-1 rounded-lg bg-deep-black border border-dark-border text-silver">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {filtered.length === 0 && !loading && (
            <div className="text-center py-20">
              <i className="ri-folder-line text-4xl text-silver-dim mb-3 block" />
              <p className="text-silver text-sm">{t("portfolio.no_projects")}</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

